import { CHART_DESTINATION_KINDS, CHART_MOVEMENT_KINDS, CHART_RETURN_HOURS } from '@wodi/domain';
import { z } from 'zod';

import { actorSchema } from './clinical-common.ts';

// Medical records chart tracking (docs/plans/phase2-medical-records-charts.md §3). Internal
// IDs only in paths; names and reasons travel in bodies and are never logged.

const name = z.string().trim().min(1).max(200);
const chartReason = z.string().trim().min(1).max(500);
const returnHours = z.number().int().min(CHART_RETURN_HOURS.min).max(CHART_RETURN_HOURS.max);

/** Where a chart is or went: a records-room location, a ward, or a department. */
export const chartPlaceSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('location'), id: z.uuid(), room: z.string(), label: z.string() }),
  z.object({ kind: z.literal('ward'), id: z.uuid(), name: z.string() }),
  z.object({ kind: z.literal('service_point'), id: z.uuid(), name: z.string() }),
]);
export type ChartPlace = z.infer<typeof chartPlaceSchema>;

export const chartMovementSchema = z.object({
  id: z.uuid(),
  kind: z.enum(CHART_MOVEMENT_KINDS),
  movedAt: z.iso.datetime(),
  place: chartPlaceSchema,
  /** Who collected it or brought it back, as written. */
  handledByName: z.string().nullable(),
  reason: z.string().nullable(),
  recordedBy: actorSchema,
  recordedAt: z.iso.datetime(),
});
export type ChartMovementView = z.infer<typeof chartMovementSchema>;

/** Where the chart is now: its latest movement. */
export const chartWhereaboutsSchema = z.object({
  state: z.enum(['in', 'out']),
  place: chartPlaceSchema,
  since: z.iso.datetime(),
  /** Out only, and only when the facility set a return period. */
  dueAt: z.iso.datetime().nullable(),
  overdue: z.boolean(),
});

export const chartSchema = z.object({
  id: z.uuid(),
  chartNumber: z.string(),
  patientId: z.uuid(),
  openedAt: z.iso.datetime(),
  openedBy: actorSchema,
  now: chartWhereaboutsSchema,
  /** Newest first. */
  movements: z.array(chartMovementSchema),
});
export type ChartView = z.infer<typeof chartSchema>;

export const chartOptionsSchema = z.object({
  locations: z.array(z.object({ id: z.uuid(), room: z.string(), label: z.string() })),
  wards: z.array(z.object({ id: z.uuid(), name: z.string() })),
  servicePoints: z.array(z.object({ id: z.uuid(), name: z.string() })),
});
export type ChartOptions = z.infer<typeof chartOptionsSchema>;

export const patientChartSchema = z.object({
  patientId: z.uuid(),
  /** Null: no chart at this facility yet. */
  chart: chartSchema.nullable(),
  /** The facility's return period in hours; null: not set up. */
  returnHours: z.number().int().nullable(),
  options: chartOptionsSchema,
});
export type PatientChartView = z.infer<typeof patientChartSchema>;

export const openChartBodySchema = z.object({
  clientRequestId: z.uuid(),
  locationId: z.uuid(),
});
export type OpenChartBody = z.infer<typeof openChartBodySchema>;

export const issueChartBodySchema = z.object({
  clientRequestId: z.uuid(),
  destination: z.object({ kind: z.enum(CHART_DESTINATION_KINDS), id: z.uuid() }),
  handledByName: name,
  reason: chartReason,
  /** Event time; now when left out. */
  movedAt: z.iso.datetime().optional(),
});
export type IssueChartBody = z.infer<typeof issueChartBodySchema>;

export const returnChartBodySchema = z.object({
  clientRequestId: z.uuid(),
  locationId: z.uuid(),
  handledByName: name.nullable(),
  reason: chartReason.nullable(),
  movedAt: z.iso.datetime().optional(),
});
export type ReturnChartBody = z.infer<typeof returnChartBodySchema>;

export const CHART_LIST_STATES = ['out', 'overdue'] as const;
export const chartListQuerySchema = z.object({ state: z.enum(CHART_LIST_STATES) });

export const chartListItemSchema = z.object({
  chartId: z.uuid(),
  chartNumber: z.string(),
  patient: z.object({ id: z.uuid(), displayName: z.string(), patientNumber: z.string() }),
  place: chartPlaceSchema,
  since: z.iso.datetime(),
  handledByName: z.string().nullable(),
  dueAt: z.iso.datetime().nullable(),
  overdue: z.boolean(),
});
export type ChartListItem = z.infer<typeof chartListItemSchema>;

export const chartListSchema = z.object({
  items: z.array(chartListItemSchema),
  returnHours: z.number().int().nullable(),
  /** More charts are out than one page shows. */
  truncated: z.boolean(),
});
export type ChartListView = z.infer<typeof chartListSchema>;

export const chartLocationSchema = z.object({
  id: z.uuid(),
  room: z.string(),
  label: z.string(),
  status: z.enum(['active', 'inactive']),
  sortOrder: z.number().int(),
});
export type ChartLocationView = z.infer<typeof chartLocationSchema>;

export const recordsSettingsSchema = z.object({
  returnHours: z.number().int().nullable(),
  locations: z.array(chartLocationSchema),
});
export type RecordsSettingsView = z.infer<typeof recordsSettingsSchema>;

export const recordsSettingsBodySchema = z.object({ returnHours: returnHours.nullable() });
export type RecordsSettingsBody = z.infer<typeof recordsSettingsBodySchema>;

export const chartLocationBodySchema = z.object({
  room: z.string().trim().min(1).max(100),
  label: z.string().trim().min(1).max(100),
  sortOrder: z.number().int().min(0).max(9999),
  status: z.enum(['active', 'inactive']),
});
export type ChartLocationBody = z.infer<typeof chartLocationBodySchema>;
